import { Link } from "react-router-dom";
import GuideLayout from "@/components/guides/GuideLayout";

const faqItems = [
  {
    question: "What is Noriday?",
    answer:
      "Noriday is a progestogen-only pill, often called a mini pill, made by Pfizer. Each tablet contains norethisterone 350 micrograms and no oestrogen. You take one tablet every day at about the same time, with no break between packs.",
  },
  {
    question: "How late can you take Noriday?",
    answer:
      "Noriday has a 3 hour window. If you are less than 3 hours late, take it as soon as you remember and you are still protected. If you are 3 or more hours late, take it as soon as you remember, take the next one at the usual time, and use condoms as your leaflet and pharmacist advise.",
  },
  {
    question: "Does Noriday stop ovulation?",
    answer:
      "Not reliably. Noriday works mainly by thickening the mucus at the cervix so sperm cannot get through, and by changing the womb lining. The leaflet says it sometimes stops the ovaries releasing an egg. Desogestrel mini pills and Slynd stop ovulation in most cycles, which is why they have longer late windows.",
  },
  {
    question: "Can I get Noriday free on the NHS?",
    answer:
      "Yes, if it is suitable for you. Progestogen-only pills are free on the NHS from a GP, a sexual health clinic, or a pharmacy providing the NHS Pharmacy Contraception Service, for people aged 16 to 54. No blood pressure reading is needed for a mini pill. Supply depends on the pharmacist's assessment.",
  },
  {
    question: "What are the side effects of Noriday?",
    answer:
      "Irregular bleeding is the most talked about change, especially in the first few months: spotting, bleeding between periods, or no periods. Other reported effects include breast discomfort, headache, stomach upsets, changes in mood, appetite, weight or sex drive, and tiredness. The product information does not give how often each happens.",
  },
  {
    question: "What if I am sick on Noriday?",
    answer:
      "If you are sick within 2 hours of a traditional mini pill like Noriday, it may not have been absorbed, so take another as soon as you can. If you cannot, treat it as a missed pill. Severe diarrhoea can also stop it working. Keep taking your pills and use condoms as the leaflet advises.",
  },
];

const tocItems = [
  { id: "what-is-it", label: "What is Noriday?" },
  { id: "how-it-works", label: "How it works" },
  { id: "compared", label: "Noriday vs other mini pills" },
  { id: "how-to-take", label: "How to take it" },
  { id: "late-pill", label: "Late pills and the 3 hour window" },
  { id: "sickness", label: "Sickness and diarrhoea" },
  { id: "side-effects", label: "Side effects" },
  { id: "who-can-take", label: "Who can take it?" },
  { id: "getting-it", label: "How to get Noriday free" },
];

const tableHead = "text-left px-3 py-2.5 font-semibold text-foreground border-b border-border";
const tableCell = "px-3 py-2 text-foreground/85 border-b border-border/50";
const tableFirst = "px-3 py-2 font-medium text-foreground border-b border-border/50";

const NoridayGuide = () => {
  return (
    <GuideLayout
      title="Noriday: What You Need to Know"
      ogImage="/og/guides/noriday.png"
      metaTitle="Noriday Mini Pill: 3 Hour Window, Side Effects and NHS Supply"
      metaDescription="Noriday (norethisterone 350 micrograms) explained: how it works, the 3 hour late window, sickness rules, side effects, how it compares with desogestrel, and NHS supply."
      canonicalPath="/guides/noriday"
      nhsSupplyTitle="Getting Noriday free on the NHS"
      nhsSupplyText="Noriday is free on the NHS if it is suitable for you. A GPhC-registered pharmacist can supply progestogen-only pills through the NHS Pharmacy Contraception Service to people aged 16 to 54. No blood pressure reading is needed for a mini pill, and supply depends on the pharmacist's assessment of your medical history."
      nhsSupplyTo="/get-the-pill-online/mini-pill/"
      nhsSupplyLabel="How to get the mini pill free on the NHS"
      breadcrumbTitle="Noriday"
      date="October 2026"
      directAnswer="Noriday is a progestogen-only pill (mini pill) containing norethisterone 350 micrograms and no oestrogen. You take it every day with no break, and it must be taken within 3 hours of your usual time.|It works mainly by thickening cervical mucus and does not reliably stop ovulation. It is free on the NHS for ages 16 to 54 if a pharmacist confirms it suits you."
      quickFacts={[
        "Mini pill: norethisterone 350 micrograms. No oestrogen.",
        "One tablet every day, no break between packs.",
        "3 hour late window, shorter than desogestrel (12 hours) or Slynd (24 hours).",
        "Free on the NHS for ages 16 to 54 if suitable. No blood pressure check needed.",
      ]}
      tocItems={tocItems}
      faqItems={faqItems}
      ctaTitle="Ask about the mini pill at a free consultation"
      ctaDescription="Book a free phone consultation with a pharmacist. If a mini pill is right for you, it is posted in discreet packaging within 1 to 3 working days."
      ctaButtonText="Book a free NHS pharmacist consultation"
      ctaSubtext="No GP appointment needed. Supply depends on the pharmacist's assessment."
      references={[
        { text: "EMC. Noriday 350 microgram Tablets. Summary of Product Characteristics.", url: "https://www.medicines.org.uk/emc/product/1146/smpc" },
        { text: "EMC. Noriday 350 microgram Tablets. Patient Information Leaflet.", url: "https://www.medicines.org.uk/emc/files/pil.1146.pdf" },
        { text: "FSRH. Progestogen-only Pills Clinical Guideline, August 2022 (amended 2023).", url: "https://www.cosrh.org/Public/Documents/fsrh-clinical-guideline-progestogen-only-pills" },
        { text: "NHS England. Community Pharmacy Advanced Service Specification: NHS Pharmacy Contraception Service. Version 3.0, October 2025.", url: "https://www.england.nhs.uk/publication/nhs-pharmacy-contraception-service/" },
        { text: "NHS. The progestogen-only pill.", url: "https://www.nhs.uk/contraception/methods-of-contraception/progestogen-only-pill/" },
      ]}
      relatedGuides={[
        { to: "/guides/what-is-the-mini-pill/", label: "What is the mini pill?" },
        { to: "/guides/desogestrel/", label: "Desogestrel" },
        { to: "/guides/cerazette/", label: "Cerazette" },
        { to: "/guides/slynd/", label: "Slynd" },
        { to: "/guides/contraceptive-pill-names/", label: "Contraceptive pill names" },
        { to: "/guides/vomiting-diarrhoea-and-the-pill/", label: "Vomiting, diarrhoea and the pill" },
        { to: "/guides/combined-pill-vs-mini-pill/", label: "Combined pill vs mini pill" },
        { to: "/guides/bleeding-on-the-pill/", label: "Bleeding on the pill" },
        { to: "/guides/which-contraceptive-pill-is-right-for-me/", label: "Which contraceptive pill is right for me?" },
      ]}
    >
      <h2 id="what-is-it">What is Noriday?</h2>
      <p>
        Noriday is a progestogen-only pill made by Pfizer. Each tablet contains norethisterone 350 micrograms, a progestogen, and no oestrogen (<a href="https://www.medicines.org.uk/emc/product/1146/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). Progestogen-only pills are often called mini pills. Noriday is a traditional mini pill, an older type than the desogestrel pills such as Cerazette.
      </p>
      <p>
        Because it contains no oestrogen, a mini pill can be an option for some people who cannot take the combined pill. Taken correctly, the mini pill is more than 99% effective, and around 91% with typical use, when pills are sometimes late or missed (<a href="https://www.nhs.uk/contraception/methods-of-contraception/progestogen-only-pill/" target="_blank" rel="noopener noreferrer">NHS</a>).
      </p>

      <h2 id="how-it-works">How does Noriday work?</h2>
      <p>
        Noriday works mainly by thickening the mucus at the cervix so sperm cannot get through. It also changes the womb lining, making it harder for a fertilised egg to implant (<a href="https://www.medicines.org.uk/emc/product/1146/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). The leaflet says it sometimes stops the ovaries releasing an egg, but it does not do this reliably.
      </p>
      <p>
        That is the main difference from desogestrel mini pills and Slynd, which stop ovulation in most cycles. Because Noriday relies on the mucus effect, which wears off within about a day, timing matters more. Read <Link to="/guides/what-is-the-mini-pill/">what is the mini pill</Link> for how the types differ.
      </p>

      <h2 id="compared">How does Noriday compare with other mini pills?</h2>
      <p>
        Noriday has the shortest late window of the mini pills supplied in the UK. The table compares the three types available through the NHS Pharmacy Contraception Service (<a href="https://www.cosrh.org/Public/Documents/fsrh-clinical-guideline-progestogen-only-pills" target="_blank" rel="noopener noreferrer">FSRH POP guideline, 2022</a>).
      </p>

      <div className="overflow-x-auto my-6 -mx-2">
        <table className="w-full text-sm border-collapse border border-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-secondary/60">
              <th className={tableHead}>Mini pill</th>
              <th className={tableHead}>Progestogen</th>
              <th className={tableHead}>Late window</th>
              <th className={tableHead}>Stops ovulation?</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Noriday", "Norethisterone 350 micrograms", "3 hours", "Not reliably"],
              ["Cerazette, Cerelle, Zelleta", "Desogestrel 75 micrograms", "12 hours", "In most cycles"],
              ["Slynd", "Drospirenone 4 milligrams", "24 hours", "In most cycles"],
            ].map(([a, b, c, d], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                <td className={tableFirst}>{a}</td>
                <td className={tableCell}>{b}</td>
                <td className={tableCell}>{c}</td>
                <td className={tableCell}>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        "Noriday suits people who are good at taking a pill at the same time every day," says Usama Ali, MPharm (GPhC: 2224726), pharmacist at Contraception Direct. "If a 3 hour window sounds hard to keep to, tell us at your consultation. A <Link to="/guides/desogestrel/">desogestrel</Link> pill or <Link to="/guides/slynd/">Slynd</Link> gives you more room."
      </p>

      <h2 id="how-to-take">How do you take Noriday?</h2>
      <p>
        You take one Noriday tablet every day at about the same time, with water, and start the next pack the day after you finish one, with no break (<a href="https://www.medicines.org.uk/emc/files/pil.1146.pdf" target="_blank" rel="noopener noreferrer">EMC patient leaflet</a>). There are no inactive tablets, so you take a hormone pill every day of the year.
      </p>
      <p>
        The leaflet advises starting on the first day of your period and using condoms for the first 7 days of the first pack. If you are switching from another method or starting at a different time, your pharmacist will tell you when to start and how long to use extra protection.
      </p>

      <h2 id="late-pill">What if you are late with a Noriday pill?</h2>
      <p>
        If you are less than 3 hours late with Noriday, take it as soon as you remember and you are still protected. At 3 or more hours late, you may not be protected: take the pill as soon as you remember, even if that means two in one day, then take the next one at the usual time (<a href="https://www.medicines.org.uk/emc/files/pil.1146.pdf" target="_blank" rel="noopener noreferrer">EMC patient leaflet</a>).
      </p>
      <p>
        The Noriday leaflet advises condoms for the next 7 days. Current FSRH guidance for traditional mini pills advises extra protection until you have taken 2 pills correctly, which is 48 hours, and emergency contraception may be considered if you had sex from the late pill until those 48 hours are complete (<a href="https://www.cosrh.org/Public/Documents/fsrh-clinical-guideline-progestogen-only-pills" target="_blank" rel="noopener noreferrer">FSRH, 2022</a>). If you are unsure which applies to you, ask a pharmacist or use our <Link to="/missed-pill/">missed pill tool</Link>.
      </p>

      <h2 id="sickness">What if you are sick or have diarrhoea on Noriday?</h2>
      <p>
        If you are sick within 2 hours of taking a traditional mini pill like Noriday, it may not have been absorbed (<a href="https://www.cosrh.org/Public/Documents/fsrh-clinical-guideline-progestogen-only-pills" target="_blank" rel="noopener noreferrer">FSRH, 2022</a>). Take another tablet as soon as you can. If you cannot keep one down, treat it as a late pill.
      </p>
      <p>
        The leaflet says Noriday may not work if you are sick or have diarrhoea, so keep taking your pills and use condoms while you are ill and for the next 7 days. Our guide to <Link to="/guides/vomiting-diarrhoea-and-the-pill/">vomiting, diarrhoea and the pill</Link> covers this for every pill type.
      </p>

      <h2 id="side-effects">What are the side effects of Noriday?</h2>
      <p>
        Changes to your bleeding are the side effect you are most likely to notice on Noriday. The product information lists cycle irregularity in the first few months, spotting, bleeding between periods and no periods (<a href="https://www.medicines.org.uk/emc/product/1146/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). It does not give how often each side effect happens.
      </p>
      <p>
        Other reported effects include breast discomfort, headache or migraine, stomach upsets, rash, tiredness, nervousness, low mood, and changes in appetite, weight or sex drive. Raised blood pressure has also been reported and usually settles after stopping. The SmPC notes that side effects in trials were less frequent than with oestrogen-containing pills.
      </p>
      <p>
        If bleeding changes or other effects bother you after a few months, speak to a pharmacist. A different mini pill may suit you better. See <Link to="/guides/bleeding-on-the-pill/">bleeding on the pill</Link> for what is normal. Get urgent help, by calling 999, if you have sudden chest pain, sudden breathlessness, a severe headache, changes in vision or speech, or severe calf pain.
      </p>

      <h2 id="who-can-take">Who can take Noriday?</h2>
      <p>
        Noriday can be supplied to people aged 16 to 54 through the NHS Pharmacy Contraception Service if the pharmacist finds it suitable. No blood pressure reading is needed for a mini pill, but the pharmacist asks about your health and medicines (<a href="https://www.england.nhs.uk/publication/nhs-pharmacy-contraception-service/" target="_blank" rel="noopener noreferrer">NHS England PCS specification</a>).
      </p>
      <p>
        The product information says Noriday is not suitable if you are or might be pregnant, have or have had a hormone-dependent cancer such as breast cancer, have severe or active liver disease, have unexplained vaginal bleeding, or have had a blood clot, stroke or heart disease (<a href="https://www.medicines.org.uk/emc/product/1146/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). If it is not suitable, the pharmacist will explain why and suggest another option or a GP or sexual health clinic. The pill does not protect against sexually transmitted infections.
      </p>

      <h2 id="getting-it">How do you get Noriday free?</h2>
      <p>
        Noriday is free on the NHS from your GP, a sexual health clinic or a pharmacy offering the NHS Pharmacy Contraception Service. With <Link to="/get-the-pill-online/">Contraception Direct</Link>, you book a free phone consultation, your pharmacist checks which mini pill suits you, and it is posted in plain packaging, usually within 1 to 3 working days.
      </p>
      <p>
        Not sure whether a mini pill or a combined pill is right for you? Read <Link to="/guides/combined-pill-vs-mini-pill/">combined pill vs mini pill</Link>, or book a consultation and talk it through.
      </p>
    </GuideLayout>
  );
};

export default NoridayGuide;
